import React, { useState } from 'react';
import { useReview } from '../../contexts/ReviewContext';
import { useAuth } from '../../contexts/AuthContext';
import { Star, Send, X, Lightbulb } from 'lucide-react';

const AddReviewForm = ({ courseId, onClose }) => {
  const { addReview } = useReview();
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      alert('Veuillez sélectionner une note');
      return;
    }
    
    if (!title.trim() || !content.trim()) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    setSubmitting(true);
    
    try {
      const reviewData = {
        courseId,
        userId: user.id,
        userName: user.name,
        userAvatar: user.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
        rating,
        title: title.trim(),
        content: content.trim()
      };

      await addReview(reviewData);

      // Reset form
      setRating(0);
      setTitle('');
      setContent('');

      onClose();
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la review:', error);
      alert('Erreur lors de l\'ajout de votre avis');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, index) => {
      const starValue = index + 1;
      const isFilled = starValue <= (hoveredRating || rating);
      
      return (
        <button
          key={index}
          type="button"
          onClick={() => setRating(starValue)}
          onMouseEnter={() => setHoveredRating(starValue)}
          onMouseLeave={() => setHoveredRating(0)}
          className="focus:outline-none focus:ring-2 focus:ring-primary/50 rounded"
        >
          <Star
            className={`w-8 h-8 transition-colors ${
              isFilled 
                ? 'text-yellow-400 fill-current' 
                : 'text-gray-300 hover:text-yellow-300'
            }`}
          />
        </button>
      );
    });
  };

  const getRatingText = (rating) => {
    const texts = {
      1: 'Très décevant',
      2: 'Décevant',
      3: 'Correct',
      4: 'Bien',
      5: 'Excellent'
    };
    return texts[rating] || '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Donner votre avis
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Note */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Votre note *
              </label>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  {renderStars()}
                </div>
                {rating > 0 && (
                  <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">
                    {getRatingText(rating)}
                  </span>
                )}
              </div>
            </div>

            {/* Titre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Titre de votre avis *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Résumez votre expérience en quelques mots..."
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                maxLength={100}
                required
              />
              <div className="text-right text-xs text-gray-500 dark:text-gray-400 mt-1">
                {title.length}/100
              </div>
            </div>

            {/* Contenu */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Votre avis détaillé *
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Partagez votre expérience avec ce cours. Qu'est-ce qui vous a plu ? Y a-t-il des points d'amélioration ?"
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                rows="6"
                maxLength={1000}
                required
              />
              <div className="text-right text-xs text-gray-500 dark:text-gray-400 mt-1">
                {content.length}/1000
              </div>
            </div>

            {/* Conseils */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                <Lightbulb className="w-4 h-4 inline mr-1 align-[-2px]" aria-hidden="true" />Conseils pour un bon avis
              </h4>
              <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                <li>• Soyez honnête et constructif</li>
                <li>• Mentionnez les points forts et les améliorations possibles</li>
                <li>• Évitez les commentaires offensants ou inappropriés</li>
                <li>• Votre avis aide les autres étudiants à choisir</li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={submitting || rating === 0 || !title.trim() || !content.trim()}
                className="flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Publication...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Publier l'avis
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddReviewForm;
